/**
 * API hooks dla redesign sandboxu - integracja z prawdziwym backendem
 * z auth (httpOnly cookie). Bez ProtectedRoute - jeśli niezalogowany,
 * komponent dostaje { error: 'Wymagane logowanie' } i renderuje login prompt.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../config/axios';

// ── Module-level cache + in-flight dedup ──────────────────────────────
// Layout / Sidebar / Today wszystkie wołają /api/auth/me + /api/tasks?limit=5
// przy każdej nawigacji. Bez cache to 4× refetch przy klik linku → nie tylko
// network waste ale też migotanie UI ("Ładowanie..." na sidebar po każdym klik).
//
// TTL 30s — wystarcza dla typowej sesji nawigacji bez stale data.
// Mutacje (POST/PUT/DELETE) wołają invalidateCache(pattern) żeby wymusić fresh.
const _cache = new Map();      // url -> { data, expires }
const _inFlight = new Map();   // url -> Promise<data>
const DEFAULT_TTL_MS = 30_000;

export function invalidateCache(urlPattern) {
  // urlPattern: string (substring) lub RegExp
  const test = urlPattern instanceof RegExp
    ? (k) => urlPattern.test(k)
    : (k) => k.includes(urlPattern);
  for (const key of _cache.keys()) {
    if (test(key)) _cache.delete(key);
  }
}

export function clearCache() { _cache.clear(); _inFlight.clear(); }

async function cachedGet(url, { signal, force = false, ttl = DEFAULT_TTL_MS } = {}) {
  const now = Date.now();
  if (!force) {
    const cached = _cache.get(url);
    if (cached && cached.expires > now) return cached.data;
    const inflight = _inFlight.get(url);
    if (inflight) return inflight;
  }
  const promise = axios.get(url, { signal })
    .then(res => {
      _cache.set(url, { data: res.data, expires: Date.now() + ttl });
      _inFlight.delete(url);
      return res.data;
    })
    .catch(err => {
      _inFlight.delete(url);
      throw err;
    });
  _inFlight.set(url, promise);
  return promise;
}

/**
 * useApi - generic hook do fetchowania danych z API.
 * Zwraca { data, loading, error, refetch }.
 *
 * @param {string|null} url - endpoint do GET. Jeśli null, hook nic nie robi.
 * @param {object} options - { transform: fn, deps: [], cache: true }
 */
export function useApi(url, { transform, deps = [], cache = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!url);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);

  const fetchData = useCallback(async (opts = {}) => {
    if (!url) return;

    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const raw = cache
        ? await cachedGet(url, { signal: controller.signal, force: opts.force })
        : (await axios.get(url, { signal: controller.signal })).data;
      const result = transform ? transform(raw) : raw;
      setData(result);
      setError(null);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      const status = err.response?.status;
      const message = err.response?.data?.error || err.message || 'Błąd serwera';
      setError({ status, message, raw: err });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, cache, ...deps]);

  useEffect(() => {
    fetchData();
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, [fetchData]);

  // refetch() z cache; refetch({ force: true }) = bypass cache
  const refetch = useCallback((opts) => fetchData(opts), [fetchData]);
  return { data, loading, error, refetch };
}

/**
 * useMultipleApi - parallel fetching wielu endpointów.
 * Zwraca { results: { key1: data, key2: data }, loading, errors }.
 *
 * @param {object} endpoints - { plots: '/api/plots', tasks: '/api/tasks?limit=5' }
 */
export function useMultipleApi(endpoints) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [refetchTick, setRefetchTick] = useState(0);
  const keysJoined = Object.keys(endpoints).sort().join('|');
  const valuesJoined = Object.values(endpoints).join('|');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    const entries = Object.entries(endpoints);
    Promise.all(
      entries.map(([key, url]) =>
        cachedGet(url, { signal: controller.signal, force: refetchTick > 0 })
          .then(data => [key, { data, error: null }])
          .catch(err => {
            if (err.name === 'CanceledError') return [key, null];
            return [key, { data: null, error: err.response?.data?.error || err.message }];
          })
      )
    ).then(pairs => {
      const data = {};
      const errs = {};
      pairs.filter(p => p !== null && p[1] !== null).forEach(([key, r]) => {
        data[key] = r.data;
        if (r.error) errs[key] = r.error;
      });
      setResults(data);
      setErrors(errs);
      setLoading(false);
    });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysJoined, valuesJoined, refetchTick]);

  const refetchAll = () => setRefetchTick(t => t + 1);
  return { results, loading, errors, refetchAll };
}

/**
 * Format daty na styl polski "23 kwi" / "23.04" / "DZIŚ"
 */
export function formatDate(dateStr, style = 'short') {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));

  const months = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
  const monthsLong = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];

  if (style === 'relative') {
    if (diffDays === 0) return 'Dziś';
    if (diffDays === 1) return 'Jutro';
    if (diffDays === -1) return 'Wczoraj';
    if (diffDays > 1 && diffDays <= 7) return `Za ${diffDays} dni`;
    if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} dni temu`;
  }
  if (style === 'short')   return `${d.getDate()} ${months[d.getMonth()]}`;
  if (style === 'numeric') return d.toLocaleDateString('pl-PL');
  if (style === 'long')    return `${d.getDate()} ${monthsLong[d.getMonth()]} ${d.getFullYear()}`;
  if (style === 'iso')     return d.toISOString().slice(0, 10);
  if (style === 'weekday') {
    const days = ['NIE', 'PON', 'WT', 'ŚR', 'CZW', 'PT', 'SOB'];
    return `${days[d.getDay()]} ${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
  return dateStr;
}

/**
 * Liczba dni od daty (dodatnia jeśli przeszłość, ujemna jeśli przyszłość).
 */
export function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

/**
 * Stan rośliny na podstawie dat - prosty heurystyk dla badge'a.
 */
export function plantStage(bed) {
  if (!bed.planted_date) return { stage: 'Plan', tone: 'dim', d: null };
  const d = daysSince(bed.planted_date);
  if (d === null) return { stage: 'Plan', tone: 'dim', d: null };
  if (d < 0)      return { stage: 'Sadzonka', tone: 'amber', d: Math.abs(d) };
  if (bed.actual_harvest_date) {
    const harvested = daysSince(bed.actual_harvest_date);
    return { stage: harvested >= 0 ? 'Zebrane' : 'Po zbiorze', tone: 'sage', d };
  }
  if (bed.expected_harvest_date) {
    const toHarvest = daysSince(bed.expected_harvest_date);
    if (toHarvest >= 0)  return { stage: 'Do zbioru', tone: 'rust',  d };
    if (toHarvest > -7)  return { stage: 'Już wkrótce', tone: 'amber', d };
    if (d < 30)          return { stage: 'Wschody',   tone: 'sage', d };
    return { stage: 'Wzrost', tone: 'sage', d };
  }
  return { stage: 'Wzrost', tone: 'sage', d };
}

/**
 * Mapowanie kategorii backendu (vegetable, herb, ...) na tone Terroir.
 */
export function categoryTone(category) {
  if (!category) return 'sage';
  if (category.includes('flower') || category === 'climber') return 'amber';
  if (category === 'fruit_tree' || category === 'fruit_bush') return 'rust';
  if (category === 'herb') return 'amber';
  return 'sage';
}

/**
 * Mapowanie task_type backendu na ikonę + ton.
 */
export function taskTypeMeta(type) {
  switch (type) {
    case 'water':   return { iconName: 'drop',     tone: 'sage' };
    case 'spray':   return { iconName: 'scissors', tone: 'rust' };
    case 'harvest': return { iconName: 'sprout',   tone: 'amber' };
    default:        return { iconName: 'sun',      tone: 'amber' };
  }
}
